import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/config/company.config";
import { Users, Search, Plus, Mail, Phone, Building, UserPlus, DollarSign, Briefcase } from "lucide-react";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  type: 'buyer' | 'seller' | 'broker';
  addedAt: any;
  activeMandates: number;
  totalPaid: number;
  status: 'active' | 'inactive' | 'completed';
}

const mockClients: Client[] = [
  { id: "1", name: "Jean-Pierre Martin", email: "jp.martin@example.com", phone: "+1 514-555-1234", company: "Martin Industries Inc.", type: "buyer", addedAt: new Date(), activeMandates: 2, totalPaid: 15000, status: "active" },
  { id: "2", name: "Sophie Tremblay", email: "s.tremblay@example.com", phone: "+1 438-555-5678", company: "Tremblay & Associés", type: "seller", addedAt: new Date(), activeMandates: 1, totalPaid: 8500, status: "active" },
  { id: "3", name: "Marc Dubois", email: "m.dubois@broker.com", phone: "+1 613-555-9012", company: "Dubois Brokerage", type: "broker", addedAt: new Date(), activeMandates: 3, totalPaid: 22000, status: "active" },
];

const translations = {
  en: { title: "My Clients", subtitle: "Manage your client relationships", addClient: "Add Client", searchClients: "Search clients...", allStatus: "All Status", active: "Active", inactive: "Inactive", totalPaid: "Total Paid", activeMandates: "Active Mandates", contact: "Contact", buyer: "Buyer", seller: "Seller", broker: "Broker", newClient: "New Client", clientName: "Client Name", email: "Email", phone: "Phone", company: "Company", clientType: "Client Type", cancel: "Cancel", save: "Save Client" },
  fr: { title: "Mes Clients", subtitle: "Gérez vos relations clients", addClient: "Ajouter Client", searchClients: "Rechercher clients...", allStatus: "Tous Statuts", active: "Actif", inactive: "Inactif", totalPaid: "Total Payé", activeMandates: "Mandats Actifs", contact: "Contact", buyer: "Acheteur", seller: "Vendeur", broker: "Courtier", newClient: "Nouveau Client", clientName: "Nom du Client", email: "Email", phone: "Téléphone", company: "Entreprise", clientType: "Type de Client", cancel: "Annuler", save: "Enregistrer" },
};

export default function PartnerClients() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "", company: "", type: "buyer" as const });

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) || client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || client.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleAddClient = () => {
    if (!newClient.name || !newClient.email) return;
    setClients([...clients, { ...newClient, id: Date.now().toString(), addedAt: new Date(), activeMandates: 0, totalPaid: 0, status: "active" }]);
    toast({ title: language === 'fr' ? "Client ajouté" : "Client Added" });
    setIsDialogOpen(false);
    setNewClient({ name: "", email: "", phone: "", company: "", type: "buyer" });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild><Button><UserPlus className="mr-2 h-4 w-4" />{t.addClient}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t.newClient}</DialogTitle></DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2"><Label>{t.clientName}</Label><Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} /></div>
              <div className="grid gap-2"><Label>{t.email}</Label><Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} /></div>
              <div className="grid gap-2"><Label>{t.phone}</Label><Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} /></div>
              <div className="grid gap-2"><Label>{t.company}</Label><Input value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setIsDialogOpen(false)}>{t.cancel}</Button><Button onClick={handleAddClient}>{t.save}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder={t.searchClients} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-full md:w-[180px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t.allStatus}</SelectItem><SelectItem value="active">{t.active}</SelectItem><SelectItem value="inactive">{t.inactive}</SelectItem></SelectContent></Select>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredClients.map((client) => (
          <Card key={client.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start gap-4">
                <Avatar className="h-12 w-12"><AvatarFallback className="bg-primary/10 text-primary">{client.name.split(' ').map(n => n[0]).join('').toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">{client.name}</CardTitle>
                  <CardDescription className="flex items-center gap-1 mt-1"><Building className="h-3 w-3" /><span className="truncate">{client.company || 'Independent'}</span></CardDescription>
                </div>
                <Badge variant={client.type === 'buyer' ? 'default' : client.type === 'seller' ? 'secondary' : 'outline'}>{t[client.type as keyof typeof t]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Mail className="h-4 w-4" /><span className="truncate">{client.email}</span></div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone className="h-4 w-4" /><span>{client.phone}</span></div>
              <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                <div className="text-center"><Briefcase className="h-4 w-4 mx-auto text-muted-foreground" /><p className="text-lg font-semibold">{client.activeMandates}</p><p className="text-xs text-muted-foreground">{t.activeMandates}</p></div>
                <div className="text-center"><DollarSign className="h-4 w-4 mx-auto text-muted-foreground" /><p className="text-lg font-semibold">{formatCurrency(client.totalPaid)}</p><p className="text-xs text-muted-foreground">{t.totalPaid}</p></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
